// `common/services/locale/locale.service.ng1.ts:37` — "31mos ago", "3 days ago".
//
// Every candidate row and every notification shows one of these, so it is the single most
// repeated string in the app, and it has a rule nobody would guess.
const SECONDS_IN_YEAR = 31_536_000
const SECONDS_IN_MONTH = 2_592_000
const SECONDS_IN_DAY = 86_400
const SECONDS_IN_HOUR = 3_600

/**
 * ⚠️ ONE AND TWO YEARS ARE SHOWN IN MONTHS, NOT YEARS. The first branch matches anything a
 * year or older, and then falls BACK to months when the whole-year count is 1 or 2 — so a
 * candidate last updated 31 months ago reads "31mos ago" rather than "2yrs ago", which is
 * exactly what the reference capture of the shortlist shows. Only from three years does it
 * switch to years.
 *
 * ⚠️ AND THE "SHORTER" FORM IS NOT A SUFFIX SWAP. `shorter` picks a different word AND drops
 * the space before it — `31mos ago` against `31 months ago` — while the plural rule stays the
 * same. Seconds are the one period whose short plural equals its short singular ('sec').
 *
 * `now` is a parameter so this is testable; upstream reads the clock directly.
 */
export function timeSince(dateEpoch: number, shorter = false, now: number = Date.now()): string {
  const seconds = Math.floor((now - dateEpoch) / 1000)

  const years = Math.floor(seconds / SECONDS_IN_YEAR)
  if (years >= 1) {
    if (years <= 2) return format(Math.floor(seconds / SECONDS_IN_MONTH), 'month', 'mo', 'mos', shorter)
    return format(years, 'year', 'yr', 'yrs', shorter)
  }

  const months = Math.floor(seconds / SECONDS_IN_MONTH)
  if (months >= 1) return format(months, 'month', 'mo', 'mos', shorter)

  const days = Math.floor(seconds / SECONDS_IN_DAY)
  if (days >= 1) return format(days, 'day', 'd', 'd', shorter)

  const hours = Math.floor(seconds / SECONDS_IN_HOUR)
  if (hours >= 1) return format(hours, 'hour', 'hr', 'hrs', shorter)

  const minutes = Math.floor(seconds / 60)
  if (minutes >= 1) return format(minutes, 'minute', 'min', 'mins', shorter)

  return format(Math.floor(seconds), 'second', 'sec', 'sec', shorter)
}

function format(value: number, period: string, short: string, shortPlural: string, shorter: boolean): string {
  if (shorter) return `${String(value)}${value === 1 ? short : shortPlural} ago`
  return `${String(value)} ${period}${value === 1 ? '' : 's'} ago`
}
